/**
 * Storezone — Branding Utilities
 *
 * Computes derived theme colors from a single hex primary color
 * and injects CSS custom properties into the DOM for the tenant theme.
 *
 * The tenant_branding table stores:
 *   primary_color, secondary_color, accent_color, sidebar_color, button_radius
 *
 * This module derives the full token ramp from primary_color so that
 * components only need to reference var(--sz-primary-*) tokens.
 */

import type { TenantBranding } from '@/types'
// Re-export from canonical location
export type { TenantBranding }


// ============================================================
// Color Math
// ============================================================

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function hexToHsl(hex: string): [number, number, number] {
  const clean = hex.replace('#', '')
  const r = parseInt(clean.slice(0, 2), 16) / 255
  const g = parseInt(clean.slice(2, 4), 16) / 255
  const b = parseInt(clean.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)]
}

function hslToHex(h: number, s: number, l: number): string {
  const lFrac = l / 100
  const a = (s * Math.min(lFrac, 1 - lFrac)) / 100
  const f = (n: number): string => {
    const k = (n + h / 30) % 12
    const color = lFrac - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

function isValidHex(hex: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(hex)
}

/** Derive the full primary color ramp from a hex primary color. */
export function derivePrimaryRamp(primaryHex: string): {
  primary: string
  primaryHover: string
  primaryActive: string
  primaryLight: string
  primarySoft: string
  primaryBorder: string
  focusRing: string
} {
  if (!isValidHex(primaryHex)) {
    primaryHex = '#2563EB' // fallback to storezone blue
  }

  const [h, s, l] = hexToHsl(primaryHex)

  return {
    primary:       primaryHex,
    primaryHover:  hslToHex(h, s, clamp(l - 10, 5, 95)),
    primaryActive: hslToHex(h, s, clamp(l - 20, 5, 90)),
    primaryLight:  hslToHex(h, clamp(s * 0.55, 10, 100), clamp(92, l, 97)),
    primarySoft:   hslToHex(h, clamp(s * 0.3, 5, 100), 97),
    primaryBorder: hslToHex(h, clamp(s * 0.7, 20, 100), clamp(78, l, 90)),
    focusRing:     `rgba(${hexToRgb(primaryHex).join(',')},0.30)`,
  }
}

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '')
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ]
}

function hexToRgba(hex: string, alpha: number): string {
  if (!isValidHex(hex)) return hex
  const rgb = hexToRgb(hex)
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
}

function getLuminance(hex: string): number {
  if (!isValidHex(hex)) return 1;
  const rgb = hexToRgb(hex);
  const a = rgb.map(v => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

// ============================================================
// DOM Injection
// ============================================================

/**
 * Apply tenant_branding to the DOM by injecting CSS custom properties
 * on the [data-theme="tenant"] scope.
 *
 * Call this when the user's active tenant branding is loaded.
 */
export function applyTenantBrandingToDOM(branding: TenantBranding): void {
  const primary = branding.primary_color ?? '#2563EB'
  const ramp = derivePrimaryRamp(primary)
  const buttonRadius = branding.button_radius ?? 8

  const vars: Record<string, string> = {
    '--sz-primary':        ramp.primary,
    '--sz-primary-hover':  ramp.primaryHover,
    '--sz-primary-active': ramp.primaryActive,
    '--sz-primary-light':  ramp.primaryLight,
    '--sz-primary-soft':   ramp.primarySoft,
    '--sz-primary-border': ramp.primaryBorder,
    '--sz-focus-ring':     ramp.focusRing,
    '--sz-radius-btn':     `${buttonRadius}px`,
  }

  // Dynamic Sidebar Contrast Logic
  if (branding.sidebar_color && isValidHex(branding.sidebar_color)) {
    const isSidebarLight = getLuminance(branding.sidebar_color) > 0.5;
    const contrastTextColor = isSidebarLight ? '#0F172A' : '#FFFFFF';
    
    vars['--sz-sidebar-text'] = contrastTextColor;
    vars['--sz-sidebar-group-text'] = hexToRgba(contrastTextColor, 0.7);
    vars['--sz-sidebar-hover-text'] = contrastTextColor;
    vars['--sz-sidebar-hover-bg'] = hexToRgba(contrastTextColor, 0.08);
    
    // Active state uses a tint of primary
    vars['--sz-sidebar-active-text'] = isSidebarLight ? ramp.primary : '#FFFFFF';
    vars['--sz-sidebar-active-bg'] = isSidebarLight ? hexToRgba(ramp.primary, 0.15) : hexToRgba(ramp.primary, 0.8);
  }

  // Find or create the tenant-theme style element
  let el = document.getElementById('sz-tenant-theme') as HTMLStyleElement | null
  if (!el) {
    el = document.createElement('style')
    el.id = 'sz-tenant-theme'
    document.head.appendChild(el)
  }

  const declarations = Object.entries(vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n')

  el.textContent = `[data-theme="tenant"] {\n${declarations}\n}`
}

/**
 * Remove injected tenant theme overrides from the DOM.
 */
export function removeTenantBrandingFromDOM(): void {
  document.getElementById('sz-tenant-theme')?.remove()
}
